import React, { useEffect, useState } from 'react';
import { Box, TextField, Fab } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { AppointmentUpdateGuestNameRequest, IAppointment } from "@ontime/types";
import AppointmentService from '../../services/AppointmentService';
import { green } from '@mui/material/colors';
import SaveIcon from '@mui/icons-material/Save';
import CheckIcon from '@mui/icons-material/Check';
import CircularProgress from '@mui/material/CircularProgress';
import { useSelector } from 'react-redux';
import { selectSelectedOrganization } from '../../stateManagement/selectedOrganizationSlice';
import { selectSelectedServiceProvider } from '../../stateManagement/selectedServiceProviderSlice';

const appointmentSchema = z.object({
    appointmentId: z.string(),
    version: z.number(),
    serviceProviderId: z.string(),
    guestFirstName: z.string().min(1, "First Name cannot be empty.").max(50, "Maximum length of First Name is 50 characters."),
    guestLastName: z.string().max(50, "Maximum length of Last Name is 50 characters.").optional(),
    startDateTime: z.string(),
    endDateTime: z.string().optional(),
});

interface AppointmentFormProps {
    closeForm: (input?: IAppointment) => void;
    updateExistData: (input: IAppointment) => void;
    appointment: IAppointment;
    disabled: boolean;

}

const AppointmentGuestNameForm: React.FC<AppointmentFormProps> = ({ closeForm, appointment, updateExistData, disabled }) => {
    const { enqueueSnackbar } = useSnackbar();

    const selectedOrganization = useSelector(selectSelectedOrganization);
    const selectedServiceProvider = useSelector(selectSelectedServiceProvider);

    const { register, handleSubmit, formState: { errors }, reset, setValue, watch } = useForm<AppointmentUpdateGuestNameRequest>({
        resolver: zodResolver(appointmentSchema),
        defaultValues: {
            appointmentId: '',
            serviceProviderId: selectedServiceProvider.serviceProviderId,
            guestFirstName: '',
            guestLastName: '',
            version: 0,
            startDateTime: '',
            endDateTime: '',
        },
    });

    useEffect(() => {
        if (appointment) {
            reset({
                appointmentId: appointment.appointmentId,
                serviceProviderId: appointment.serviceProviderId,
                guestFirstName: appointment.guestFirstName,
                guestLastName: appointment.guestLastName,
                version: appointment.version,
                startDateTime: appointment.startDateTime,
                endDateTime: appointment.endDateTime,
            });

        }
    }, [appointment, reset]);

    const [guestLoading, setGuestLoading] = useState(false);
    const handleSave = async (input: AppointmentUpdateGuestNameRequest) => {
        setGuestLoading(true);

        try {

            const response = await AppointmentService.UpdateGuestName(selectedOrganization.organizationId, selectedServiceProvider.serviceProviderId, input);

            updateExistData({ ...appointment, guestFirstName: response.guestFirstName, guestLastName: response.guestLastName, version: response.version });
        } catch (error) {
            console.error(error, { variant: 'error' });
        } finally {
            setGuestLoading(false);
        }
    };

    const guestFirstName = watch('guestFirstName');
    const guestLastName = watch('guestLastName');

    const isDirty =
        guestFirstName !== appointment.guestFirstName ||
        guestLastName !== appointment.guestLastName;

    return (
        <form onSubmit={handleSubmit(handleSave)} noValidate>
            <Box display="flex" alignItems="center" gap={2} mt={2}>
                <TextField
                    fullWidth
                    label="First Name"
                    InputLabelProps={{ shrink: true }}
                    {...register('guestFirstName')}
                    error={!!errors.guestFirstName}
                    helperText={errors.guestFirstName?.message}
                    disabled={guestLoading || disabled}
                />
                <TextField
                    fullWidth
                    label="Last Name"
                    InputLabelProps={{ shrink: true }}
                    {...register('guestLastName')}
                    error={!!errors.guestLastName}
                    helperText={errors.guestLastName?.message}
                    disabled={guestLoading || disabled}
                />
                <Box sx={{ position: 'relative' }}>
                    <Fab disabled={guestLoading || !isDirty || disabled}
                        size='small' aria-label="save" color="primary" type="submit">
                        {!isDirty ? <CheckIcon /> : <SaveIcon />}
                    </Fab>
                    {guestLoading && (
                        <CircularProgress
                            size={48}
                            sx={{
                                color: green[500],
                                position: 'absolute',
                                top: -4,
                                left: -4,
                                zIndex: 1,
                            }}
                        />
                    )}
                </Box>
            </Box>
        </form>
    );
};

export default AppointmentGuestNameForm;